import { useCallback, useEffect, useId, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/** Peak displacement in px right under the cursor while it's moving */
const MAX_SCALE = 10;
/** Share of the peak kept while the cursor rests on the image (a faint lens) */
const REST_STRENGTH = 0.08;
/** Cursor speed (px/s) that counts as "full" movement */
const FULL_SPEED = 1400;
/** Radius of the distorted spot, as a fraction of the image width (clamped) */
const RADIUS_FRACTION = 0.2;
const MIN_RADIUS = 110;
const MAX_RADIUS = 240;

/**
 * Soft radial spot used to mask the noise: opaque in the middle, fading to nothing at the edge.
 * Outside it the displacement map is neutral grey, so only the area around the cursor ripples.
 */
const SPOT_URL = `data:image/svg+xml;utf8,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><defs><radialGradient id="g"><stop offset="0" stop-color="#fff" stop-opacity="1"/><stop offset="0.45" stop-color="#fff" stop-opacity="0.75"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient></defs><rect width="100" height="100" fill="url(#g)"/></svg>',
)}`;

/**
 * Cursor-driven liquid distortion for an image. The ripple lives only around the pointer, trails it with a
 * little lag, swells with how fast you move and settles to a faint lens when you stop. The noise itself only
 * evolves as the cursor travels, so nothing plays on its own. The filter is attached only while it's visible.
 */
export function useFluidHover(reduce: boolean | null) {
  const filterId = `fluid-${useId().replace(/:/g, '')}`;
  const targetRef = useRef<HTMLDivElement>(null);
  const turbRef = useRef<SVGFETurbulenceElement>(null);
  const spotRef = useRef<SVGFEImageElement>(null);
  const dispRef = useRef<SVGFEDisplacementMapElement>(null);

  const s = useRef({
    hovering: false,
    tx: 0, // cursor target (local px)
    ty: 0,
    x: 0, // smoothed spot centre
    y: 0,
    travelled: 0, // px moved since last frame
    energy: 0, // 0..1 movement energy
    amount: 0, // 0..1 final strength
    phase: 0, // noise evolution, advanced only by movement
    raf: 0,
    last: 0,
  });

  const tick = useCallback((now: number) => {
    const st = s.current;
    const dt = Math.min((now - st.last) / 1000, 0.05) || 0.016;
    st.last = now;

    // Spot glides after the cursor
    const follow = 1 - Math.exp(-6 * dt);
    st.x += (st.tx - st.x) * follow;
    st.y += (st.ty - st.y) * follow;

    // Movement energy: rises quickly with speed, decays smoothly when the cursor slows
    const speed = st.travelled / dt;
    st.travelled = 0;
    const want = Math.min(speed / FULL_SPEED, 1);
    st.energy += (want - st.energy) * (1 - Math.exp(-(want > st.energy ? 2.5 : 1.2) * dt));
    st.phase += speed * dt * 0.0015;

    const goal = st.hovering ? REST_STRENGTH + (1 - REST_STRENGTH) * st.energy : 0;
    st.amount += (goal - st.amount) * (1 - Math.exp(-3 * dt));

    const el = targetRef.current;
    const w = el?.offsetWidth ?? 600;
    const r = Math.min(Math.max(w * RADIUS_FRACTION, MIN_RADIUS), MAX_RADIUS);
    const spot = spotRef.current;
    if (spot) {
      spot.setAttribute('x', (st.x - r).toFixed(1));
      spot.setAttribute('y', (st.y - r).toFixed(1));
      spot.setAttribute('width', (r * 2).toFixed(1));
      spot.setAttribute('height', (r * 2).toFixed(1));
    }
    turbRef.current?.setAttribute(
      'baseFrequency',
      `${(0.007 + 0.0015 * Math.sin(st.phase)).toFixed(5)} ${(0.009 + 0.0015 * Math.cos(st.phase * 0.8)).toFixed(5)}`,
    );
    dispRef.current?.setAttribute('scale', (st.amount * MAX_SCALE).toFixed(2));

    if (!st.hovering && st.amount < 0.004) {
      st.amount = 0;
      st.energy = 0;
      if (el) el.style.filter = '';
      st.raf = 0;
      return;
    }
    st.raf = requestAnimationFrame(tick);
  }, []);

  const local = (e: ReactPointerEvent) => {
    const r = targetRef.current?.getBoundingClientRect();
    return r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: 0, y: 0 };
  };

  const onEnter = useCallback(
    (e: ReactPointerEvent) => {
      if (reduce || e.pointerType !== 'mouse') return;
      const st = s.current;
      const p = local(e);
      // Start the spot right under the cursor (no slide in from the last spot)
      st.tx = st.x = p.x;
      st.ty = st.y = p.y;
      st.hovering = true;
      if (targetRef.current) targetRef.current.style.filter = `url(#${filterId})`;
      if (!st.raf) {
        st.last = performance.now();
        st.raf = requestAnimationFrame(tick);
      }
    },
    [reduce, filterId, tick],
  );

  const onMove = useCallback((e: ReactPointerEvent) => {
    const st = s.current;
    if (!st.hovering) return;
    const p = local(e);
    st.travelled += Math.hypot(p.x - st.tx, p.y - st.ty);
    st.tx = p.x;
    st.ty = p.y;
  }, []);

  const onLeave = useCallback(() => {
    s.current.hovering = false;
  }, []);

  useEffect(() => () => cancelAnimationFrame(s.current.raf), []);

  return { filterId, spotUrl: SPOT_URL, targetRef, turbRef, spotRef, dispRef, onEnter, onMove, onLeave };
}
